'use client';

import type { ReactNode } from 'react';

import {
  ServicePriceLookup,
  useServicePriceLookupInitial,
} from '@/components/service/ServicePriceLookup';
import { ServiceStatusLookup } from '@/features/service/repair-details';
import { devWarn } from '@/lib/utils/devLog';

import { PlaceholderServiceServiceRequest } from './ServiceRequest/PlaceholderServiceServiceRequest';
import type { HeadingData } from '../Heading';

/** Available placeholder service types */
export type PlaceholderServiceType =
  | 'IdentifyWatch'
  | 'ServiceStatus'
  | 'DropOff'
  | 'ServicePricesSearch'
  | 'Print'
  | 'CaliberNonManufacture'
  | 'CaliberTab'
  | 'BuckleTable'
  | 'ArchivesForm'
  | 'ServiceRequest';

export interface PlaceholderServiceProps {
  /** Service type to render */
  type?: PlaceholderServiceType | string | null;
  /** Heading for the service section */
  placeholderHeading?: HeadingData | null;
  /** Optional CMS rich-text intro below the heading */
  subtext?: { json?: unknown } | null;
  /** Storefront locale for service components that fetch ECS data */
  locale?: string;
  /** Mirrors `FEATURE_SERVICE_REQUEST` — required for ServiceStatus lookup. */
  serviceRequestEnabled?: boolean;
  /** Market ECS availability — gates the send-by-mail channel on ServiceRequest. */
  ecsAvailable?: boolean;
  /** Additional props passed through to the service component */
  [key: string]: unknown;
}

/**
 * Registry of service type components
 * Add implementations here as they're ported
 */
const serviceComponents: Partial<
  Record<PlaceholderServiceType, React.ComponentType<PlaceholderServiceProps>>
> = {};

function renderServiceStatus(serviceRequestEnabled?: boolean): ReactNode {
  if (!serviceRequestEnabled) {
    return null;
  }
  return <ServiceStatusLookup />;
}

function renderServiceRequest(params: {
  locale?: string;
  placeholderHeading?: HeadingData | null;
  subtext?: { json?: unknown } | null;
  ecsAvailable?: boolean;
  serviceRequestEnabled?: boolean;
}): ReactNode {
  const { ecsAvailable, locale, placeholderHeading, serviceRequestEnabled, subtext } = params;
  if (!locale) {
    if (process.env.NODE_ENV === 'development') {
      devWarn('ServiceRequest requires CMS locale — check CMSRenderer extraData wiring');
    }
    return null;
  }

  return (
    <PlaceholderServiceServiceRequest
      locale={locale}
      placeholderHeading={placeholderHeading}
      subtext={subtext}
      ecsAvailable={ecsAvailable === true}
      serviceRequestEnabled={serviceRequestEnabled === true}
    />
  );
}

function renderServicePricesSearch(params: {
  locale?: string;
  placeholderHeading?: HeadingData | null;
  subtext?: { json?: unknown } | null;
  initialData: ReturnType<typeof useServicePriceLookupInitial>;
}): ReactNode {
  const { initialData, locale, placeholderHeading, subtext } = params;
  if (!locale || !initialData) {
    if (process.env.NODE_ENV === 'development') {
      devWarn(
        'ServicePricesSearch requires CMS locale and SSR servicePrices extraData — check the page route and CMSRenderer wiring'
      );
    }
    return null;
  }

  return (
    <ServicePriceLookup
      initialData={initialData.initialData}
      initialError={initialData.initialError}
      initialRefNumber={initialData.initialRefNumber}
      locale={locale}
      placeholderHeading={placeholderHeading}
      subtext={subtext}
    />
  );
}

function renderDevelopmentStub(type: string): ReactNode {
  return (
    <div
      style={{
        padding: '2rem',
        backgroundColor: '#f0f0f0',
        borderRadius: '4px',
        textAlign: 'center',
      }}
      data-testid="PlaceholderServiceStub"
    >
      <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>PlaceholderService: {type}</p>
      <p style={{ fontSize: '0.875rem', color: '#666' }}>This service type needs implementation</p>
    </div>
  );
}

/**
 * Placeholder Service component
 *
 * Routes to specific service components based on the `type` prop.
 * This is a factory/router component that delegates to implementations.
 *
 * Supported types:
 * - IdentifyWatch
 * - ServiceStatus
 * - DropOff
 * - ServicePricesSearch
 * - Print
 * - CaliberNonManufacture
 * - CaliberTab
 * - BuckleTable
 * - ArchivesForm
 * - ServiceRequest
 *
 * @example
 * <PlaceholderService
 *   type="Print"
 *   placeholderHeading={{ text: 'Print this page', headingType: 'h3' }}
 * />
 */
export function PlaceholderService(props: PlaceholderServiceProps) {
  const { ecsAvailable, locale, placeholderHeading, serviceRequestEnabled, subtext, type } = props;
  const initialData = useServicePriceLookupInitial();

  if (!type) {
    return null;
  }

  if (type === 'ServiceStatus') {
    return renderServiceStatus(serviceRequestEnabled);
  }

  if (type === 'ServiceRequest') {
    return renderServiceRequest({
      locale,
      placeholderHeading,
      subtext,
      ecsAvailable,
      serviceRequestEnabled,
    });
  }

  if (type === 'ServicePricesSearch') {
    return renderServicePricesSearch({
      locale,
      placeholderHeading,
      subtext,
      initialData,
    });
  }

  const ServiceComponent = serviceComponents[type as PlaceholderServiceType];

  if (ServiceComponent) {
    return <ServiceComponent {...props} />;
  }

  if (process.env.NODE_ENV === 'development') {
    return renderDevelopmentStub(type);
  }

  return null;
}

PlaceholderService.displayName = 'PlaceholderService';

/**
 * Register a service component implementation
 *
 * @example
 * // In the service component file:
 * registerServiceComponent('Print', PlaceholderPrint);
 */
export function registerServiceComponent(
  type: PlaceholderServiceType,
  component: React.ComponentType<PlaceholderServiceProps>
) {
  serviceComponents[type] = component;
}
